import {
  Alert,
  AlertDescription,
  AlertTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Spinner
} from 'design-system';

import ChartSummary from '@/features/charts/ChartSummary';
import type { ExtendedSeriesOptionsType } from '@/features/charts/types/highcharts';

import FullScreenLegend from './FullScreenLegend';
import useFullScreenChart from './useFullScreenChart';

const FullScreenChart = ({
  chartSeries,
  error,
  loading,
  isLightMode,
  tabName
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  error: boolean;
  loading: boolean;
  isLightMode: boolean;
  tabName: string;
}) => {
  const hasSeries = Boolean(chartSeries?.length);
  const { appearance, containerRef, legendSeries, mode, toggleSeriesVisibility } =
    useFullScreenChart({
      chartId: tabName,
      chartSeries: chartSeries ?? [],
      lightMode: isLightMode ? 'light' : 'dark'
    });
  const summary = {
    kind: 'stock' as const,
    label: tabName,
    totalSeries: legendSeries.length,
    visibleSeries: legendSeries.filter(series => series.visible).length
  };

  if (error && !hasSeries) {
    return (
      <section
        aria-label={`${tabName} data unavailable`}
        className='flex min-h-[420px] w-full flex-1 flex-col justify-center gap-3'
      >
        <Alert>
          <AlertTitle>Chart data unavailable.</AlertTitle>
          <AlertDescription>
            We couldn't retrieve your data. Please try again later.
          </AlertDescription>
        </Alert>
        <ChartSummary {...summary} />
      </section>
    );
  }

  if (loading || !chartSeries) {
    return (
      <section
        aria-label={`${tabName} chart`}
        className='flex min-h-[420px] w-full flex-1 items-center justify-center'
      >
        <Spinner
          aria-label='Loading chart'
          className='size-10'
        />
      </section>
    );
  }

  if (!hasSeries) {
    return (
      <section
        aria-label={`${tabName} data unavailable`}
        className='flex min-h-[420px] w-full flex-1'
      >
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to display this data.</EmptyTitle>
            <EmptyDescription>No chart series were returned.</EmptyDescription>
            <ChartSummary {...summary} />
          </EmptyHeader>
        </Empty>
      </section>
    );
  }

  return (
    <section
      aria-label={`${tabName} chart workspace`}
      className='gx-fullscreen-stock-chart w-full min-h-0'
      data-appearance-mode={mode}
      style={{
        backgroundColor: appearance.background,
        color: appearance.foreground
      }}
    >
      {error && (
        <Alert className='mb-2'>
          <AlertTitle>Some chart data may be unavailable.</AlertTitle>
          <AlertDescription>
            We couldn't retrieve all chart data. Please try again later.
          </AlertDescription>
        </Alert>
      )}
      <div className='sr-only'>
        <ChartSummary {...summary} />
      </div>
      <div
        ref={containerRef}
        aria-hidden='true'
        data-testid='fullscreen-stock-chart'
        style={{
          backgroundColor: appearance.background,
          color: appearance.foreground,
          height: 'calc(100vh - 330px)',
          minHeight: 420,
          width: '100%'
        }}
      />
      <FullScreenLegend
        series={legendSeries}
        onToggleSeries={toggleSeriesVisibility}
      />
    </section>
  );
};

export default FullScreenChart;
